/**
 * Accueil, selon le rôle :
 *  - formateur : le menu principal des trois espaces (pédagogique, apprenant, formation) — CdC oral du 23/09/2026 ;
 *  - admin : le pipeline « Tous les dossiers » — sept colonnes, de gauche à droite (F-CRM-01, F-CRM-02) ;
 *    le formateur retrouve ce même pipeline dans « Espace formation → Mes dossiers » (`MesDossiers`) ;
 *  - apprenant : ses formations, et ce qui est attendu de lui.
 */
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Archive, ArrowRight, Banknote, BookOpen, Columns3, FolderCheck, GraduationCap, Landmark, List, CalendarDays, Check, FolderKanban, FolderPlus, LayoutGrid, Lock, Search, UsersRound } from "lucide-react";
import { api, dateFr, heuresFr, type AccueilFormateur, type Json, type ListeDossiers } from "../api";
import type { lireAccueilDossier } from "@/serveur/services/accueil-apprenant";
import { CouvertureCarte } from "../ui/Couverture";
import { useActeur } from "../session";
import { Alerte, Carte as Surface, Chargement, classesLienBouton, cx, EtatVide, Etiquette, ILLUSTRATIONS, Indicateur, TitrePage } from "../ui/base";
import { ESPACES_FORMATEUR } from "../navigation";
import { compterParGroupe, groupeDe, GROUPES_SYNTHESE, type GroupeSynthese } from "@/domaine/pipeline/synthese";
import { Icone } from "./Cadre";
import { PublicationsATraiter } from "./Publication";
import { BandeauRole } from "../ui/BandeauRole";
import { CarteDossier } from "./CarteDossier";

export function Accueil() {
  const acteur = useActeur();
  const liste = useQuery({ queryKey: ["dossiers"], queryFn: () => api.get<ListeDossiers>("/dossiers") });
  if (liste.isPending) return <Chargement />;
  if (liste.error) return <p className="text-danger">{liste.error.message}</p>;
  if (acteur.role === "apprenant") return <MesFormations liste={liste.data} prenom={acteur.nom.split(" ")[0] ?? ""} />;
  // Formateur : le menu principal des trois espaces (cahier des charges oral du 23/09/2026). Admin : le pipeline.
  if (acteur.role === "formateur") return <MenuEspaces liste={liste.data} prenom={acteur.nom.split(" ")[0] ?? ""} />;
  // Organisme : bandeau d'ambiance, file « Publications à traiter » (31/10/2026), puis le pipeline.
  return (
    <>
      <BandeauRole role="admin" />
      <PublicationsATraiter />
      <Pipeline liste={liste.data} admin />
    </>
  );
}

/** « Mes dossiers » du formateur (espace formation) — et « Tous les dossiers » pour l'admin qui suivrait ce lien. */
export function MesDossiers() {
  const acteur = useActeur();
  const liste = useQuery({ queryKey: ["dossiers"], queryFn: () => api.get<ListeDossiers>("/dossiers") });
  if (liste.isPending) return <Chargement />;
  if (liste.error) return <p className="text-danger">{liste.error.message}</p>;
  return <Pipeline liste={liste.data} admin={acteur.role === "admin"} />;
}

const TONS_ESPACE = { pedagogique: "bg-accent-doux text-accent-fort", apprenant: "bg-attente-doux text-attente-encre", formation: "bg-papier-3 text-encre", administration: "bg-papier-3 text-encre" } as const;

/**
 * « À traiter » : ce qui attend le formateur, sans ouvrir le pipeline — brouillons à compléter, pièces en attente de
 * retour. Calculé sur la liste des dossiers déjà chargée (aucune règle nouvelle : statut et compteur de pièces).
 */
function ATraiter({ liste }: { liste: ListeDossiers }) {
  const lignes = liste.dossiers
    .filter((d) => !d.archive && !d.archive_manuel)
    .map((d) => {
      const attente = d.pieces_total - d.pieces_validees;
      const raison = d.sous_statut === "brouillon" ? "Brouillon à compléter, puis à soumettre à l'organisme" : d.sous_statut !== "en_cours_validation" && attente > 0 ? (attente === 1 ? "1 pièce en attente de retour" : `${attente} pièces en attente de retour`) : null;
      return raison ? { d, raison } : null;
    })
    .filter((x): x is { d: ListeDossiers["dossiers"][number]; raison: string } => x !== null)
    // Les brouillons d'abord (rien n'avance sans le formateur), puis par date de début.
    .sort((a, b) => Number(b.d.sous_statut === "brouillon") - Number(a.d.sous_statut === "brouillon") || (a.d.formation_date_debut || "9999").localeCompare(b.d.formation_date_debut || "9999"));
  if (lignes.length === 0) return null;
  return (
    <section aria-labelledby="titre-a-traiter" className="mb-6 rounded-lg border border-attente/60 bg-attente-doux/50 p-4 sm:p-5">
      <h2 id="titre-a-traiter" className="flex items-center gap-2 text-base font-semibold">
        À traiter <span className="chiffres rounded-full bg-attente px-2 text-[12px] font-semibold text-attente-encre">{lignes.length}</span>
      </h2>
      <ul className="mt-3 divide-y divide-attente/30">
        {lignes.slice(0, 5).map(({ d, raison }) => (
          <li key={d.id}>
            <Link to="/dossiers/$id" params={{ id: d.id }} className="group flex items-center gap-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  <span className="chiffres text-encre-3">{d.dossier_reference}</span> · {d.formation_titre}
                </span>
                <span className="block truncate text-[13px] text-encre-2">
                  {raison}
                  {d.apprenants.length > 0 && ` — ${d.apprenants.join(", ")}`}
                </span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-encre-3 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
      {lignes.length > 5 && (
        <Link to="/dossiers" className="mt-2 inline-block text-sm font-medium text-accent underline-offset-4 hover:underline">
          Voir les {lignes.length} dossiers dans le pipeline
        </Link>
      )}
    </section>
  );
}

const jourLong = (iso: string) => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" }).format(d);
};

/**
 * Lot 1.5 — « Votre parcours » : six jalons constatés par le serveur, le niveau atteint, et l'astuce du prochain
 * jalon avec le lien pour agir (repris du tableau de bord du premier SaaS).
 */
function Parcours({ p }: { p: AccueilFormateur["parcours"] }) {
  const prochain = p.jalons.find((j) => j.cle === p.prochain);
  return (
    <Surface className="overflow-hidden">
      <div className="grid md:grid-cols-[minmax(0,1fr)_180px]">
        <section aria-labelledby="titre-parcours" className="p-5 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="titre-parcours" className="text-lg font-semibold">
              Votre parcours
            </h2>
            <span className="text-sm text-encre-2">
              Niveau <strong className="font-semibold text-accent-fort">{p.niveau}</strong> · <span className="chiffres">{p.atteints}/{p.total}</span> jalons
            </span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-papier-3" role="progressbar" aria-label="Jalons atteints" aria-valuemin={0} aria-valuemax={p.total} aria-valuenow={p.atteints}>
            <div className="h-full rounded-full bg-valide transition-[width] duration-500" style={{ width: `${Math.round((p.atteints / p.total) * 100)}%` }} />
          </div>
          <ol className="mt-4 grid grid-cols-1 gap-2 min-[420px]:grid-cols-2 sm:grid-cols-3">
            {p.jalons.map((j) => (
              <li key={j.cle} className={cx("flex items-start gap-2 rounded-md border px-2.5 py-2 text-[13px] leading-snug", j.atteint ? "border-valide/30 bg-valide-doux text-encre" : j.cle === p.prochain ? "border-attente bg-attente-doux text-encre" : "border-trait text-encre-3")}>
                {j.atteint ? <Check className="mt-px size-4 shrink-0 text-valide" strokeWidth={3} aria-hidden /> : <Lock className="mt-px size-3.5 shrink-0" aria-hidden />}
                <span>
                  {j.titre}
                  <span className="sr-only">{j.atteint ? " : atteint" : " : à venir"}</span>
                </span>
              </li>
            ))}
          </ol>
          {prochain ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-papier-2 px-4 py-3">
              <p className="min-w-0 text-sm">
                <span className="font-semibold">Prochaine étape : {prochain.titre.toLowerCase()}.</span> <span className="text-encre-2">{prochain.astuce}</span>
              </p>
              <Link to={prochain.vers} className={classesLienBouton("secondaire", "sm")}>
                J'y vais <ArrowRight className="size-3.5" aria-hidden />
              </Link>
            </div>
          ) : (
            <p className="mt-4 text-sm text-encre-2">Tous les jalons sont atteints : vous maîtrisez le portage de bout en bout.</p>
          )}
        </section>
        <img src={ILLUSTRATIONS.accueil} alt="" width={180} height={320} className="hidden h-full w-full object-cover object-[50%_20%] md:block" />
      </div>
    </Surface>
  );
}

/** Lot 1.5 — les séances des 30 prochains jours, jour par jour, chacune menant à son dossier. */
function Planning({ a }: { a: AccueilFormateur }) {
  const MAX = 8;
  let restantes = MAX;
  const affiches = a.planning
    .map((j) => {
      const seances = j.seances.slice(0, Math.max(0, restantes));
      restantes -= seances.length;
      return { ...j, seances };
    })
    .filter((j) => j.seances.length > 0);
  const masquees = a.indicateurs.seances_a_venir - (MAX - Math.max(0, restantes));
  return (
    <section id="planning" aria-labelledby="titre-planning" className="scroll-mt-24">
      <Surface className="h-full p-5 sm:p-6">
        <h2 id="titre-planning" className="text-lg font-semibold">
          Planning des {a.horizon_jours} prochains jours
        </h2>
        {affiches.length === 0 ? (
          <EtatVide image={ILLUSTRATIONS.lecture} titre="Aucune séance prévue">
            Les séances de vos dossiers en cours s'afficheront ici, jour par jour.
          </EtatVide>
        ) : (
          <ol className="mt-3 space-y-3">
            {affiches.map((j) => (
              <li key={j.date}>
                <h3 className="text-[13px] font-semibold text-encre-2 first-letter:uppercase">{jourLong(j.date)}</h3>
                <ul className="mt-1 space-y-1">
                  {j.seances.map((x) => (
                    <li key={`${x.dossier_id}-${x.heure_debut}`}>
                      <Link to="/dossiers/$id" params={{ id: x.dossier_id }} className="group flex items-start gap-3 rounded-md px-2 py-1.5 hover:bg-papier-2">
                        <span className="chiffres w-[92px] shrink-0 pt-px text-[13px] font-medium text-accent-fort">
                          {x.heure_debut}–{x.heure_fin}
                        </span>
                        <span className="min-w-0 flex-1 text-sm">
                          <span className="block truncate font-medium">{x.formation_titre}</span>
                          <span className="block truncate text-[12.5px] text-encre-3">
                            <span className="chiffres">{x.dossier_reference}</span>
                            {x.apprenants.length > 0 && ` · ${x.apprenants.join(", ")}`}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
        {masquees > 0 && (
          <Link to="/dossiers" className="mt-3 inline-block text-sm font-medium text-accent underline-offset-4 hover:underline">
            Et {masquees === 1 ? "1 autre séance" : `${masquees} autres séances`} dans vos dossiers
          </Link>
        )}
      </Surface>
    </section>
  );
}

function MenuEspaces({ liste, prenom }: { liste: ListeDossiers; prenom: string }) {
  const accueil = useQuery({ queryKey: ["accueil-formateur"], queryFn: () => api.get<AccueilFormateur>("/accueil/formateur") });
  const aTraiter = liste.dossiers.filter((d) => d.sous_statut === "brouillon").length;
  const a = accueil.data;
  // Revue M-2 : une seule définition de « en cours », celle du serveur (refus de financement et archives exclus).
  const enCours = a?.indicateurs.dossiers_en_cours ?? liste.dossiers.filter((d) => !d.archive && d.sous_statut !== "refus_financement").length;
  const indicateur: Partial<Record<string, string>> = {
    "/dossiers": enCours === 0 ? "aucun dossier en cours" : enCours === 1 ? "1 dossier en cours" : `${enCours} dossiers en cours`,
    "/dossiers/nouveau": aTraiter > 0 ? (aTraiter === 1 ? "1 brouillon à finaliser" : `${aTraiter} brouillons à finaliser`) : "",
  };
  return (
    <>
      <BandeauRole role="formateur" />
      <TitrePage
        titre={`Bonjour ${prenom}`}
        soustitre="Ce qui vous attend, vos chiffres du moment et vos prochaines séances."
        aide="parcours"
        actions={
          <Link to="/dossiers/nouveau" className={classesLienBouton("signal")}>
            <FolderPlus className="size-4" aria-hidden />
            Nouveau dossier
          </Link>
        }
      />
      <ATraiter liste={liste} />
      {/* Revue M-1 : jamais d'échec silencieux — chargement annoncé, erreur affichée. */}
      {accueil.isPending && <Chargement libelle="Chargement de vos chiffres…" />}
      {accueil.error && (
        <div className="mb-6">
          <Alerte ton="danger" titre="Vos chiffres et votre planning n'ont pas pu être chargés">{accueil.error.message}</Alerte>
        </div>
      )}
      {a && (
        <div className="mb-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicateur libelle="Dossiers en cours" valeur={a.indicateurs.dossiers_en_cours} detail={a.indicateurs.brouillons > 0 ? `dont ${a.indicateurs.brouillons} brouillon${a.indicateurs.brouillons > 1 ? "s" : ""}` : "dans le pipeline"} icone={<FolderKanban className="size-4" aria-hidden />} ton="accent" vers="/dossiers" />
            <Indicateur libelle="Séances (30 jours)" valeur={a.indicateurs.seances_a_venir} detail={a.indicateurs.heures_a_venir > 0 ? `${heuresFr(a.indicateurs.heures_a_venir)} de formation` : "aucune programmée"} icone={<CalendarDays className="size-4" aria-hidden />} ton="attente" vers="/" hash="planning" />
            <Indicateur libelle="Apprenants suivis" valeur={a.indicateurs.apprenants_suivis} detail="dans vos dossiers en cours" icone={<UsersRound className="size-4" aria-hidden />} vers="/repertoire" />
            <Indicateur libelle="Formations" valeur={a.indicateurs.formations} detail="à votre catalogue" icone={<BookOpen className="size-4" aria-hidden />} vers="/formations" />
          </div>
          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
            <Parcours p={a.parcours} />
            <Planning a={a} />
          </div>
        </div>
      )}
      {accueil.isPending && <Chargement libelle="Chargement de vos chiffres…" />}
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-encre-2">
        <LayoutGrid className="size-4" aria-hidden /> Vos trois espaces
      </h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {ESPACES_FORMATEUR.map((e) => (
          <section key={e.cle} aria-label={e.titre} className="flex flex-col rounded-lg border border-trait bg-carte p-5 shadow-carte">
            <h3 className="flex items-center gap-2.5 text-[17px] font-semibold">
              <span className={cx("grid size-9 place-items-center rounded-md", TONS_ESPACE[e.cle])}>
                <Icone cle={e.liens[0]!.icone} className="size-[18px]" />
              </span>
              {e.titre}
            </h3>
            <p className="mt-2 text-sm text-encre-2">{e.description}</p>
            <ul className="mt-4 flex flex-1 flex-col gap-1.5">
              {e.liens.map((l) => (
                <li key={l.vers}>
                  <Link to={l.vers} className="group flex items-center gap-3 rounded-md border border-trait px-3 py-2.5 transition-colors duration-150 hover:border-accent/60 hover:bg-papier-2">
                    <Icone cle={l.icone} className="size-4 shrink-0 text-encre-3 group-hover:text-accent" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{l.libelle}</span>
                      {(indicateur[l.vers] || l.aide) && <span className="block truncate text-xs text-encre-3">{indicateur[l.vers] || l.aide}</span>}
                    </span>
                    <ArrowRight className="size-4 shrink-0 text-encre-3 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

type Vue = "colonnes" | "liste";
const CLE_VUE = "s4m.pipeline.vue";
/** Vue mémorisée par navigateur ; à défaut : liste tant que les sept colonnes ne tiennent pas sans défilement horizontal (moins de 1800 px), colonnes au-delà. */
function vueInitiale(): Vue {
  try {
    const v = localStorage.getItem(CLE_VUE);
    if (v === "colonnes" || v === "liste") return v;
  } catch {
    /* stockage indisponible : choix par la largeur */
  }
  return typeof window !== "undefined" && window.matchMedia?.("(max-width: 1799px)").matches ? "liste" : "colonnes";
}

const ICONES_GROUPE: Record<GroupeSynthese, React.ReactNode> = {
  a_valider: <FolderCheck className="size-4" aria-hidden />,
  financement: <Landmark className="size-4" aria-hidden />,
  formation: <GraduationCap className="size-4" aria-hidden />,
  paiement: <Banknote className="size-4" aria-hidden />,
};

function Pipeline({ liste, admin }: { liste: ListeDossiers; admin: boolean }) {
  const [recherche, setRecherche] = useState("");
  // Validé le 04/11/2026 (H-140) : dossiers archivés masqués par défaut, la case les fait réapparaître.
  const [archives, setArchives] = useState(false);
  const [groupe, setGroupe] = useState<GroupeSynthese | null>(null);
  const [vue, setVueEtat] = useState<Vue>(vueInitiale);
  const setVue = (v: Vue) => {
    setVueEtat(v);
    try {
      localStorage.setItem(CLE_VUE, v);
    } catch {
      /* préférence non mémorisée : sans conséquence */
    }
  };
  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return liste.dossiers.filter((d) => (archives || !(d.archive || d.archive_manuel)) && (!groupe || groupeDe(d.sous_statut) === groupe) && (!q || [d.dossier_reference, d.formation_titre, d.formateur, ...d.apprenants].join(" ").toLowerCase().includes(q)));
  }, [liste.dossiers, recherche, archives, groupe]);
  const aValider = liste.dossiers.filter((d) => d.sous_statut === "en_cours_validation").length;
  const comptes = compterParGroupe(liste.dossiers.map((d) => d.sous_statut));

  return (
    <>
      <TitrePage
        titre={admin ? "Tous les dossiers" : "Mes dossiers"}
        aide={admin ? undefined : "pipeline"}
        soustitre={
          admin
            ? aValider > 0
              ? aValider === 1 ? "1 dossier attend votre validation." : `${aValider} dossiers attendent votre validation.`
              : "Aucun dossier en attente de validation."
            : vue === "liste" ? "Chaque dossier avance de l'étape A (création) à l'étape G (archivage)." : "Chaque carte avance de gauche à droite, de la création à l'archivage."
        }
        actions={
          !admin && (
            <Link to="/dossiers/nouveau" className={classesLienBouton("signal")}>
              <FolderPlus className="size-4" aria-hidden />
              Nouveau dossier
            </Link>
          )
        }
      />

      {admin && liste.dossiers.length > 0 && (
        <section aria-label="Synthèse des dossiers" className="mb-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {GROUPES_SYNTHESE.map((g) => (
              <Indicateur key={g.cle} libelle={g.libelle} valeur={comptes[g.cle]} detail={g.detail} icone={ICONES_GROUPE[g.cle]} ton={g.cle === "a_valider" && comptes.a_valider > 0 ? "attente" : g.cle === "paiement" ? "accent" : "neutre"} actif={groupe === g.cle} onClick={() => setGroupe(groupe === g.cle ? null : g.cle)} />
            ))}
          </div>
          {groupe && (
            <p className="mt-2 text-sm text-encre-2" role="status">
              Filtre : {GROUPES_SYNTHESE.find((g) => g.cle === groupe)!.libelle} ({filtres.length}).{" "}
              <button type="button" onClick={() => setGroupe(null)} className="font-medium text-accent underline-offset-4 hover:underline">
                Afficher tous les dossiers
              </button>
            </p>
          )}
        </section>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <label className="relative min-w-0 basis-full sm:max-w-xs sm:basis-auto sm:flex-1">
          <span className="sr-only">Rechercher un dossier</span>
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-encre-3" aria-hidden />
          <input
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Référence, formation, apprenant…"
            className="h-10 w-full rounded-sm border border-trait-fort bg-carte pr-3 pl-9 text-sm placeholder:text-encre-3 hover:border-encre-3 focus:border-accent focus:ring-[3px] focus:ring-accent/40 focus:outline-none"
          />
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-encre-2">
          <input type="checkbox" checked={archives} onChange={(e) => setArchives(e.target.checked)} className="size-4 accent-(--color-accent)" />
          Afficher les dossiers archivés
        </label>
        <div role="group" aria-label="Affichage du pipeline" className="ml-auto inline-flex rounded-sm border border-trait-fort bg-carte p-0.5">
          {([["colonnes", "Colonnes", Columns3], ["liste", "Liste", List]] as const).map(([v, libelle, Ic]) => (
            <button key={v} type="button" aria-pressed={vue === v} onClick={() => setVue(v)} className={cx("inline-flex h-8 items-center gap-1.5 rounded-xs px-2.5 text-[13px] font-medium", vue === v ? "bg-accent-doux text-accent-fort" : "text-encre-2 hover:bg-papier-2")}>
              <Ic className="size-4" aria-hidden />
              {libelle}
            </button>
          ))}
        </div>
      </div>

      {liste.dossiers.length === 0 ? (
        <EtatVide
          image={ILLUSTRATIONS.bureau}
          titre="Aucun dossier pour l'instant"
          action={!admin && <Link to="/dossiers/nouveau" className={classesLienBouton("signal")}>Créer mon premier dossier</Link>}
        >
          {admin ? "Les dossiers créés par les formateurs apparaîtront ici." : "Un dossier réunit un apprenant, son entreprise et une formation. Créez d'abord une formation et une fiche apprenant si ce n'est pas fait."}
        </EtatVide>
      ) : vue === "liste" ? (
        <div className="space-y-4">
          {liste.etapes.map((etape) => {
            const cartes = filtres.filter((d) => d.etape === etape.cle);
            return (
              <section key={etape.cle} aria-label={etape.titre} className="rounded-lg bg-papier-2 p-2.5">
                <h2 className="flex items-baseline justify-between gap-2 px-1.5 pt-0.5 pb-2 text-[14px] font-semibold">
                  <span>
                    <span className="mr-1.5 text-encre-3">{etape.cle}</span>
                    {etape.titre}
                  </span>
                  <span className="chiffres text-xs text-encre-3">{cartes.length}</span>
                </h2>
                {cartes.length === 0 ? (
                  <p className="px-1.5 pb-1 text-xs text-encre-3">Aucun dossier</p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3">
                    {cartes.map((d) => (
                      <CarteDossier key={d.id} d={d} admin={admin} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:-mx-8 sm:px-8">
          <div className="grid auto-cols-[minmax(248px,1fr)] grid-flow-col gap-3 max-lg:auto-cols-[78vw] sm:max-lg:auto-cols-[320px]">
            {liste.etapes.map((etape) => {
              const cartes = filtres.filter((d) => d.etape === etape.cle);
              return (
                <section key={etape.cle} aria-label={etape.titre} className="flex min-w-0 flex-col rounded-lg bg-papier-2 p-2.5">
                  <header className="flex items-baseline justify-between gap-2 px-1.5 pt-1 pb-3">
                    <h2 className="min-w-0 text-[13px] leading-tight font-semibold text-encre">
                      <span className="mr-1.5 text-encre-3">{etape.cle}</span>
                      {etape.titre}
                    </h2>
                    <span className="chiffres shrink-0 text-xs font-semibold text-encre-3">{cartes.length}</span>
                  </header>
                  <div className="flex flex-1 flex-col gap-2">
                    {cartes.map((d) => (
                      <CarteDossier key={d.id} d={d} admin={admin} />
                    ))}
                    {cartes.length === 0 && <p className="rounded-md border border-dashed border-trait-fort px-3 py-6 text-center text-xs text-encre-3">Aucun dossier</p>}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

type AccueilDossier = Json<Awaited<ReturnType<typeof lireAccueilDossier>>>;

/** Même requête (et même cache) que l'en-tête de la fiche formation : l'ouverture de la formation est immédiate. */
function CouvertureDeLaFormation({ dossierId }: { dossierId: string }) {
  const accueil = useQuery({
    queryKey: ["accueil-dossier", dossierId],
    queryFn: () => api.get<AccueilDossier>(`/accueil-apprenant/dossiers/${dossierId}`),
  });
  return <CouvertureCarte accueil={accueil.data?.accueil ?? null} formateur={accueil.data?.formateur ?? null} />;
}

function MesFormations({ liste, prenom }: { liste: ListeDossiers; prenom: string }) {
  return (
    <div className="mx-auto max-w-3xl">
      <BandeauRole role="apprenant" />
      <TitrePage titre={`Bonjour ${prenom}`} soustitre="Retrouvez ici vos formations et les documents qui attendent votre retour." />
      {/* Mot d'accueil illustré (inventaire §7.3 prio 5) : la marche à suivre en trois temps, avant la liste. */}
      <Surface className="mb-6 flex items-center gap-4 overflow-hidden p-4 sm:gap-5 sm:p-5">
        <img src={ILLUSTRATIONS.formation} alt="" width={88} height={88} className="size-20 shrink-0 rounded-full object-cover object-top sm:size-22" />
        <ol className="min-w-0 space-y-1 text-sm text-encre-2">
          <li><strong className="text-encre">1.</strong> Ouvrez votre formation.</li>
          <li><strong className="text-encre">2.</strong> Suivez la « prochaine étape » : signez ou déposez les documents demandés.</li>
          <li><strong className="text-encre">3.</strong> Vos supports de cours apparaissent dès l'accord de financement.</li>
        </ol>
      </Surface>
      {liste.dossiers.length === 0 ? (
        <EtatVide image={ILLUSTRATIONS.lecture} titre="Aucune formation pour l'instant">Dès que votre formateur vous aura inscrit à une formation, elle apparaîtra ici.</EtatVide>
      ) : (
        <ul className="space-y-3">
          {liste.dossiers.map((d) => {
            const enAttente = d.pieces_total - d.pieces_validees;
            return (
              <li key={d.id}>
                <Link to="/dossiers/$id" params={{ id: d.id }} className="group block overflow-hidden rounded-md border border-trait bg-carte shadow-carte transition-[border-color] duration-150 hover:border-accent/60">
                  {/* 04/11/2026 : la couverture et la photo du formateur, comme en tête de la formation (façon Notion). */}
                  <CouvertureDeLaFormation dossierId={d.id} />
                <div className="flex items-center gap-4 p-4 pt-8 sm:p-5 sm:pt-9">
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[17px] leading-snug font-semibold">{d.formation_titre}</p>
                    <p className="chiffres mt-1 text-sm text-encre-2">
                      {d.formation_date_debut ? `Du ${dateFr(d.formation_date_debut)} au ${dateFr(d.formation_date_fin)}` : "Dates à venir"} · {d.formateur}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {d.archive ? (
                        <Etiquette>
                          <Archive className="mr-1 size-3" aria-hidden />
                          Terminé
                        </Etiquette>
                      ) : enAttente > 0 ? (
                        <Etiquette ton="attente">{enAttente === 1 ? "1 document à traiter" : `${enAttente} documents à traiter`}</Etiquette>
                      ) : (
                        <Etiquette ton="accent">Vous êtes à jour</Etiquette>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="size-5 shrink-0 text-encre-3 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-accent" aria-hidden />
                </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
